/**
 * Guest (non-authenticated) cart and wishlist persistence.
 *
 * Both are arrays of listing UUID strings kept in localStorage so that a
 * guest's picks survive reloads and can be merged into the account on login.
 * Kept dependency-free so both ducks and helpers can import it without
 * creating import cycles.
 */

export const LOCAL_CART_ITEMS_KEY = 'localCartItems';
export const LOCAL_WISHLIST_KEY = 'localWishlistItems';

// Sanity cap: a guest cart/wishlist beyond this is corrupt or hostile, and
// an oversized array would make the login merge's updateProfile fail on
// every fetchCurrentUser until storage is cleared.
const MAX_IDS = 500;

const sanitize = value =>
  Array.isArray(value) ? value.filter(x => typeof x === 'string').slice(0, MAX_IDS) : [];

const readIds = key => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    return sanitize(JSON.parse(window.localStorage.getItem(key)));
  } catch (e) {
    return [];
  }
};

// Writes are best-effort: localStorage.setItem throws in Safari private
// mode / when quota is exceeded / under storage-disabled policies, and the
// wishlist toggle calls this straight from an onClick.
const writeIds = (key, ids) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(sanitize(ids)));
  } catch (e) {
    // ignore — the in-memory Redux mirror still reflects the action
  }
};

const removeKey = key => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    // ignore
  }
};

export const getLocalCartItems = () => readIds(LOCAL_CART_ITEMS_KEY);
export const setLocalCartItems = ids => writeIds(LOCAL_CART_ITEMS_KEY, ids);
export const removeLocalCartItems = () => removeKey(LOCAL_CART_ITEMS_KEY);

// How many of each listing a guest wants: { listingId: quantity }.
export const LOCAL_CART_QUANTITIES_KEY = 'localCartQuantities';
export const sanitizeQuantities = value =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(
        Object.entries(value)
          .filter(([id, q]) => typeof id === 'string' && Number.isInteger(q) && q > 0)
          .slice(0, MAX_IDS)
      )
    : {};
export const getLocalCartQuantities = () => {
  if (typeof window === 'undefined') return {};
  try {
    return sanitizeQuantities(JSON.parse(window.localStorage.getItem(LOCAL_CART_QUANTITIES_KEY)));
  } catch (e) {
    return {};
  }
};
export const setLocalCartQuantities = quantities => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      LOCAL_CART_QUANTITIES_KEY,
      JSON.stringify(sanitizeQuantities(quantities))
    );
  } catch (e) {
    // ignore, as writeIds does
  }
};
export const removeLocalCartQuantities = () => removeKey(LOCAL_CART_QUANTITIES_KEY);

export const getLocalWishlist = () => readIds(LOCAL_WISHLIST_KEY);
export const setLocalWishlist = ids => writeIds(LOCAL_WISHLIST_KEY, ids);
export const removeLocalWishlist = () => removeKey(LOCAL_WISHLIST_KEY);
